import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageSquarePlus, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { useAuthStore } from '@/auth';
import {
  usePatient,
  usePatientClinicianTips,
  useSendClinicianTip,
} from '@/hooks/queries';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { extractApiErrorMessage } from '@/lib/apiError';

/**
 * Send a short tip to a single patient. v1 lives at
 * `clinician/src/components/sendTip.tsx`. Tip surfaces in the patient
 * mobile app under App Settings → Tips.
 *
 * Caps the message at 300 chars (matches v1) — the mobile app's
 * Settings list cell truncates around that length and patients tend
 * to skim, so longer text gets diminishing returns.
 *
 * The history list below the editor pulls the last 12 months of tips
 * from this patient — gives the clinician context (so they don't
 * repeat themselves) without an extra click.
 */
type Props = {
  patientId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const MAX_LEN = 300;

export const SendTipDialog = ({ patientId, open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const clinician = useAuthStore((s) => s.clinician);
  const clinicianId = clinician?.id ?? null;
  const patient = usePatient(patientId);
  const tipsQuery = usePatientClinicianTips(open ? patientId : null);
  const send = useSendClinicianTip();

  const [message, setMessage] = useState('');

  // Reset the editor every time the dialog opens. Carrying drafts
  // across opens would be confusing (a clinician might re-open expecting
  // a fresh start) and there's no persistent-draft requirement.
  useEffect(() => {
    if (open) {
      setMessage('');
      send.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const recipientName = [patient.data?.first_name, patient.data?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  const handleSend = async () => {
    const text = message.trim();
    if (!text || clinicianId == null) return;
    try {
      await send.mutateAsync({
        body: {
          created_by_clinician_id: clinicianId,
          message: text,
          patient_ids: [patientId],
        },
      });
      toast({ title: t('Tip sent') });
      track(ANALYTICS_EVENTS.PATIENT_TIP_SENT, { tip_length: text.length });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t("Couldn't send your message"),
        description: extractApiErrorMessage(err)
          ?? t('Please try again. If this keeps happening, contact support.'),
        variant: 'destructive',
      });
    }
  };

  const remaining = MAX_LEN - message.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="h-4 w-4" />
            {t('Send a tip')}
          </DialogTitle>
          {recipientName && (
            <DialogDescription>
              {t('to {{name}}', { name: recipientName })}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="tip-message">
              {t('Tip text (max {{n}} characters)', { n: MAX_LEN })}
            </Label>
            <textarea
              id="tip-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
              placeholder={t('e.g. Nice job on hitting your fibre target this week!')}
              rows={4}
              className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              disabled={send.isPending}
              autoFocus
            />
            <div className="flex justify-end text-[0.6875rem] tabular-nums text-muted-foreground">
              {remaining}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('History (last 12 months)')}
            </Label>
            <div className="max-h-40 overflow-y-auto rounded-md border bg-muted/20 p-2 text-xs">
              {tipsQuery.isLoading ? (
                <Skeleton className="h-12 w-full" />
              ) : tipsQuery.isError ? (
                <p className="italic text-destructive">
                  {extractApiErrorMessage(tipsQuery.error)
                    ?? t('Failed to load history.')}
                </p>
              ) : !tipsQuery.data?.tips || tipsQuery.data.tips.length === 0 ? (
                <p className="italic text-muted-foreground">{t('No tips yet.')}</p>
              ) : (
                <ul className="space-y-1.5">
                  {tipsQuery.data.tips.map((tip, idx) => (
                    <li key={idx} className="border-b border-dashed last:border-0 pb-1.5 last:pb-0">
                      <span className="font-semibold">
                        {new Date(tip.created_time).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                        :
                      </span>{' '}
                      <span className="whitespace-pre-wrap">{tip.message}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={send.isPending}
          >
            {t('Cancel')}
          </Button>
          <Button onClick={handleSend} disabled={!message.trim() || send.isPending}>
            {send.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="mr-1 h-3.5 w-3.5" />
            )}
            {t('Send tip')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
